import React, { useEffect, useState } from 'react';
import { deviceTrustAPI, companiesAPI } from '../services/api';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield, ShieldCheck, ShieldX, Ban, RefreshCw, Search, Loader2, MonitorSmartphone, Cpu, Info,
} from 'lucide-react';

const STATUS_VARIANT = {
  approved: 'success', pending: 'warning', blocked: 'destructive', revoked: 'secondary',
};

const DeviceTrust = () => {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';

  const [tab, setTab] = useState('pending');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [q, setQ] = useState('');
  const [report, setReport] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Config editor
  const [configCompany, setConfigCompany] = useState('__global__');
  const [config, setConfig] = useState(null);
  const [configBusy, setConfigBusy] = useState(false);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany !== 'all') params.company_id = filterCompany;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSource !== 'all') params.source = filterSource;
      if (q) params.q = q;
      const [rowsRes, cRes] = await Promise.all([
        tab === 'pending' ? deviceTrustAPI.pending() : deviceTrustAPI.list(params),
        companiesAPI.getAll(),
      ]);
      setDevices(rowsRes.data || []);
      setCompanies(cRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load devices');
    } finally { setLoading(false); }
  };

  const fetchReport = async () => {
    try {
      const r = await deviceTrustAPI.report();
      setReport(r.data);
    } catch (e) { /* ignore */ }
  };

  const fetchConfig = async (cid) => {
    setConfigBusy(true);
    try {
      const r = await deviceTrustAPI.configCompany(cid);
      setConfig(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load config');
    } finally { setConfigBusy(false); }
  };

  useEffect(() => { fetchDevices(); fetchReport(); /* eslint-disable-next-line */ }, [tab, filterCompany, filterStatus, filterSource]);
  useEffect(() => { if (tab === 'config') fetchConfig(configCompany); /* eslint-disable-next-line */ }, [tab, configCompany]);

  const saveConfig = async () => {
    setConfigBusy(true);
    try {
      const payload = {
        enabled: !!config.enabled,
        tofu_enabled: !!config.tofu_enabled,
        max_devices_per_user: Number(config.max_devices_per_user || 5),
        require_reapproval_days: Number(config.require_reapproval_days || 0),
        notify_it_emails: Array.isArray(config.notify_it_emails)
          ? config.notify_it_emails
          : (config.notify_it_emails || '').split(',').map((s) => s.trim()).filter(Boolean),
      };
      const r = await deviceTrustAPI.updateConfig(configCompany, payload);
      setConfig(r.data);
      toast.success('Configuration saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setConfigBusy(false); }
  };

  const applyAction = async (action) => {
    if (!selectedDevice) return;
    setActionBusy(true);
    try {
      const fn = deviceTrustAPI[action];
      await fn(selectedDevice.id, actionNote);
      toast.success(`Device ${action}`);
      setSelectedDevice(null); setActionNote('');
      fetchDevices(); fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActionBusy(false); }
  };

  const rows = devices;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield size={22} />Device Trust</h1>
          <p className="text-muted-foreground text-sm">Approve, block, and audit the devices allowed to access Pride Global HRMS.</p>
        </div>
        <Button variant="outline" onClick={() => { fetchDevices(); fetchReport(); }} data-testid="device-refresh-btn">
          <RefreshCw size={14} className="mr-2" />Refresh
        </Button>
      </div>

      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Approved</div><div className="text-2xl font-bold text-green-600" data-testid="stat-approved">{report.totals.approved}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Pending</div><div className="text-2xl font-bold text-yellow-600" data-testid="stat-pending">{report.totals.pending}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Blocked</div><div className="text-2xl font-bold text-red-600" data-testid="stat-blocked">{report.totals.blocked}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Revoked</div><div className="text-2xl font-bold text-muted-foreground" data-testid="stat-revoked">{report.totals.revoked}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Users with device</div><div className="text-2xl font-bold" data-testid="stat-users-with-device">{report.users_with_approved_device}</div></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Approvals</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All Devices</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Policy Config</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3">
          <DeviceTable rows={rows} loading={loading} onOpen={setSelectedDevice} />
        </TabsContent>

        <TabsContent value="all" className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, hostname, IP…" className="pl-9" data-testid="device-search" />
            </div>
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-48" data-testid="device-filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36" data-testid="device-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-36" data-testid="device-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any source</SelectItem>
                <SelectItem value="browser">Browser</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchDevices} data-testid="device-apply-filters">Apply</Button>
          </div>
          <DeviceTable rows={rows} loading={loading} onOpen={setSelectedDevice} />
        </TabsContent>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Shield size={18} />Device Trust Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Scope</Label>
                  <Select value={configCompany} onValueChange={setConfigCompany}>
                    <SelectTrigger data-testid="config-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isSuper && <SelectItem value="__global__">Global (master switch)</SelectItem>}
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={() => fetchConfig(configCompany)} disabled={configBusy} data-testid="config-reload-btn">
                  <RefreshCw size={14} className="mr-1" />Reload
                </Button>
              </div>
              {configBusy && !config ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
              ) : config ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between border rounded p-3">
                    <div>
                      <Label className="text-sm">Enforcement enabled</Label>
                      <p className="text-xs text-muted-foreground">Turn on device gating for this scope.</p>
                    </div>
                    <Switch checked={!!config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="config-enabled-switch" />
                  </div>
                  <div className="flex items-center justify-between border rounded p-3">
                    <div>
                      <Label className="text-sm">Trust-on-first-use (TOFU)</Label>
                      <p className="text-xs text-muted-foreground">Auto-trust new devices while awaiting IT approval.</p>
                    </div>
                    <Switch checked={!!config.tofu_enabled} onCheckedChange={(v) => setConfig({ ...config, tofu_enabled: v })} data-testid="config-tofu-switch" />
                  </div>
                  <div className="border rounded p-3 space-y-1">
                    <Label className="text-sm">Max devices per user</Label>
                    <Input type="number" min={1} max={20} value={config.max_devices_per_user || 5} onChange={(e) => setConfig({ ...config, max_devices_per_user: e.target.value })} data-testid="config-max-devices" />
                  </div>
                  <div className="border rounded p-3 space-y-1">
                    <Label className="text-sm">Re-approval interval (days, 0 = never)</Label>
                    <Input type="number" min={0} max={365} value={config.require_reapproval_days || 0} onChange={(e) => setConfig({ ...config, require_reapproval_days: e.target.value })} data-testid="config-reapproval-days" />
                  </div>
                  <div className="border rounded p-3 space-y-1 md:col-span-2">
                    <Label className="text-sm">IT notification emails (comma-separated)</Label>
                    <Input
                      value={Array.isArray(config.notify_it_emails) ? config.notify_it_emails.join(', ') : (config.notify_it_emails || '')}
                      onChange={(e) => setConfig({ ...config, notify_it_emails: e.target.value })}
                      placeholder="it@company.com, sec-ops@company.com"
                      data-testid="config-notify-emails"
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-2">
                    <Button onClick={saveConfig} disabled={configBusy} data-testid="config-save-btn">
                      {configBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Save
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Device detail + action modal */}
      <Dialog open={!!selectedDevice} onOpenChange={(o) => { if (!o) { setSelectedDevice(null); setActionNote(''); } }}>
        <DialogContent className="max-w-2xl" data-testid="device-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MonitorSmartphone size={18} />Device details</DialogTitle>
            <DialogDescription>Review and take action on this device.</DialogDescription>
          </DialogHeader>
          {selectedDevice && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant={STATUS_VARIANT[selectedDevice.status] || 'secondary'}>{selectedDevice.status}</Badge>
                <Badge variant="outline">{selectedDevice.source}</Badge>
                {selectedDevice.label && <span className="font-medium">{selectedDevice.label}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div><span className="text-foreground font-medium">User:</span> {selectedDevice.user_email}</div>
                <div><span className="text-foreground font-medium">Fingerprint:</span> <span className="font-mono">{(selectedDevice.fingerprint || '').slice(0, 24)}…</span></div>
                <div><span className="text-foreground font-medium">First seen:</span> {selectedDevice.first_seen_at}</div>
                <div><span className="text-foreground font-medium">Last seen:</span> {selectedDevice.last_seen_at}</div>
                <div><span className="text-foreground font-medium">First IP:</span> {selectedDevice.first_seen_ip || '—'}</div>
                <div><span className="text-foreground font-medium">Last IP:</span> {selectedDevice.last_seen_ip || '—'}</div>
                <div><span className="text-foreground font-medium">Platform:</span> {selectedDevice.platform || selectedDevice.os || '—'}</div>
                <div><span className="text-foreground font-medium">Timezone:</span> {selectedDevice.timezone || '—'}</div>
                {selectedDevice.source === 'agent' && (
                  <>
                    <div><span className="text-foreground font-medium">Hostname:</span> {selectedDevice.hostname || '—'}</div>
                    <div><span className="text-foreground font-medium">OS:</span> {selectedDevice.os} {selectedDevice.os_version}</div>
                    <div><span className="text-foreground font-medium">Device UUID:</span> <span className="font-mono">{selectedDevice.device_uuid || '—'}</span></div>
                    <div><span className="text-foreground font-medium">BIOS S/N:</span> <span className="font-mono">{selectedDevice.bios_serial || '—'}</span></div>
                    <div className="col-span-2"><span className="text-foreground font-medium">MACs:</span> <span className="font-mono">{(selectedDevice.mac_addresses || []).join(', ') || '—'}</span></div>
                  </>
                )}
              </div>
              <div>
                <Label className="text-xs">Note (optional, saved to audit log)</Label>
                <Input value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="e.g. 'Trusted employee laptop'" data-testid="device-action-note" />
              </div>
              <div className="rounded bg-muted/40 p-2 flex items-start gap-2 text-xs text-muted-foreground">
                <Info size={14} className="mt-0.5" />
                Approving grants HRMS access; blocking rejects login; revoking requires re-registration.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { setSelectedDevice(null); setActionNote(''); }} data-testid="device-close-btn">Close</Button>
            <Button variant="secondary" onClick={() => applyAction('revoke')} disabled={actionBusy} data-testid="device-revoke-btn"><Ban size={14} className="mr-1" />Revoke</Button>
            <Button variant="destructive" onClick={() => applyAction('block')} disabled={actionBusy} data-testid="device-block-btn"><ShieldX size={14} className="mr-1" />Block</Button>
            <Button onClick={() => applyAction('approve')} disabled={actionBusy} data-testid="device-approve-btn">
              {actionBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <ShieldCheck size={14} className="mr-1" />}Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DeviceTable = ({ rows, loading, onOpen }) => {
  if (loading) return <div className="text-sm text-muted-foreground p-6 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading devices…</div>;
  if (!rows || rows.length === 0) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No devices found.</CardContent></Card>;
  return (
    <div className="border rounded divide-y" data-testid="device-list">
      {rows.map((d) => (
        <button key={d.id} onClick={() => onOpen(d)} className="w-full text-left p-3 hover:bg-muted/30 transition" data-testid={`device-row-${d.id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            {d.source === 'agent' ? <Cpu size={16} className="text-muted-foreground" /> : <MonitorSmartphone size={16} className="text-muted-foreground" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{d.label || d.hostname || d.user_email}</span>
                <Badge variant={STATUS_VARIANT[d.status] || 'secondary'}>{d.status}</Badge>
                <Badge variant="outline" className="text-xs">{d.source}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {d.user_email} · {d.platform || d.os || '—'} · {d.last_seen_ip || '—'} · last seen {d.last_seen_at}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

export default DeviceTrust;
